/**
 * Fire mode — the read, the two writes, and the "held by fire mode"
 * discrimination every command path needs.
 *
 * WHY THE READ EXISTS AT ALL (and is not "the live stream already tells
 * us"). The alarm is published to `rmf/v1/sites/{site}/emergency/fire/
 * state` and that key has a storage, but a storage is not a replay: the
 * Zenoh REST/SSE plugin declares a plain subscriber for the streaming
 * request, and a subscriber only ever receives samples PUBLISHED AFTER
 * it is declared. Every other category in this app already works around
 * that with a REST seed — `robotsQueryOptions`/`tasksQueryOptions` fetch
 * a snapshot and let the stream overlay it, and
 * `v1-zenoh-client.ts::fetchCategorySamples` exists solely to GET the
 * stored samples the stream will not resend. Fire mode had no such
 * seed, so a browser opened DURING an alarm saw a clear building and
 * waited for a transition that had already happened. `FireAlarmProvider`
 * consumes this and prefers whichever of the two was updated most
 * recently.
 *
 * WHY THE CAPABILITY GATE IS NOT `cap.enabled`. `GET /capabilities`
 * evaluates through `CapabilityPolicy.evaluate`, which adds
 * `unsupported_by_rmf` to anything outside the site's
 * `DeploymentBinding.certified_capabilities` — the set of things the
 * site's RMF deployment is certified to do. Fire mode is a nexsoss-side
 * freeze RMF has no notion of, so no binding will ever certify it and
 * `enabled` is permanently false for both emergency capabilities (the
 * api-server's own `api/emergency.py` says exactly this, which is why
 * IT checks the role table directly instead of calling `evaluate`).
 * Gating the buttons on `enabled` would therefore disable the fire
 * trigger and the recovery at every site forever. This gates on the
 * same question the server asks — does a role this caller holds carry
 * the capability — read off `disabled_reasons`.
 */
import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '../../auth/AuthContext';
import { roleHoldsCapability } from '../../auth/role-preview';
import { normaliseRoles } from '../../auth/roles';
import { getCurrentToken } from '../../auth/token-store';
import { SiteContext } from '../../state/SiteContext';
import { useSafeSiteId } from '../../state/useActiveSite';
import { capabilitiesQueryOptions, tasksQueryOptions } from '../query-options';
import { alertsKey, alertSummaryKey } from '../scope';
import { fetchFireMode, resolveFireMode, triggerFireMode } from '../v1-client';
import type {
  CapabilityId,
  FireModeState,
  FireResolveRequest,
  FireResolveResponse,
  FireTriggerRequest,
  SiteId,
} from '../v1';

/**
 * Deliberately NOT under `FIRE_ALARM_KEY`. `useLiveData`'s legacy
 * overlay calls `qc.removeQueries({ queryKey: [FIRE_ALARM_KEY], exact:
 * false })` on every site change, which would drop this seed as
 * collateral on the very transition that needs it most.
 */
export const FIRE_MODE_KEY = 'fire-mode' as const;
export const fireModeKey = (siteId: string) => [FIRE_MODE_KEY, siteId] as const;

/**
 * Server-side fire state for the active site.
 *
 * Disabled until the token store has a bearer token: `apiFetch` reads
 * it directly, so a request issued before auth bootstraps is a
 * guaranteed 401. The reactive half of the condition is the auth
 * context's own token, which changes when the session does; the store
 * read is what the request actually depends on.
 */
export function useFireModeState() {
  const siteId = useSafeSiteId();
  // Not `useAuth()`: this runs inside `FireAlarmProvider`, which is
  // mounted in tests and stories that supply no auth context at all,
  // and a throwing hook there would take the whole shell down.
  const auth = useContext(AuthContext);
  return useQuery<FireModeState>({
    queryKey: fireModeKey(siteId),
    queryFn: () => fetchFireMode(siteId as SiteId),
    enabled: Boolean(siteId) && Boolean(auth?.token) && getCurrentToken() !== null,
    // The freeze is server-owned and latched: nothing in the browser
    // can end it, and the live key covers the transitions. This poll is
    // the backstop for a stream that dropped a sample, not the primary
    // path, so it is slow on purpose.
    staleTime: 15_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useTriggerFireMode() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<FireModeState, Error, FireTriggerRequest>({
    mutationFn: (body) => triggerFireMode(siteId as SiteId, body),
    onSuccess: (state) => {
      // The response IS the new state, so seed it rather than making
      // the dialog wait a round trip to stop saying "not active".
      qc.setQueryData(fireModeKey(siteId), state);
      void qc.invalidateQueries({ queryKey: alertsKey(siteId) });
      void qc.invalidateQueries({ queryKey: alertSummaryKey(siteId) });
      void qc.invalidateQueries({ queryKey: tasksQueryOptions(siteId ? (siteId as SiteId) : null).queryKey });
    },
  });
}

export function useResolveFireMode() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<FireResolveResponse, Error, FireResolveRequest>({
    mutationFn: (body) => resolveFireMode(siteId as SiteId, body),
    onSuccess: () => {
      // Refetched, not seeded: the resolve response reports what the
      // recovery sweep DID, and a partial failure leaves live task
      // state disagreeing with any state we could synthesise here.
      void qc.invalidateQueries({ queryKey: fireModeKey(siteId) });
      void qc.invalidateQueries({ queryKey: alertsKey(siteId) });
      void qc.invalidateQueries({ queryKey: alertSummaryKey(siteId) });
      void qc.invalidateQueries({ queryKey: tasksQueryOptions(siteId ? (siteId as SiteId) : null).queryKey });
    },
  });
}

export interface EmergencyCapabilities {
  /** From the capability matrix; once it has `failed`, from the role
   *  mirror (`roleHoldsCapability`) instead. */
  canTrigger: boolean;
  canReset: boolean;
  /** False until the capability matrix has been read at all. Callers
   *  must not render an emergency control while this is false and the
   *  read has not failed — an ungated trigger button is worse than a
   *  late one. */
  loaded: boolean;
  /** The matrix could not be read and nothing is cached. Lets the fire
   *  panel say so instead of "checking" for ever (2026-10-31). */
  failed: boolean;
}

/** Reasons that mean "this caller may not, ever" rather than "not right
 *  now". Everything else on `disabled_reasons` is a live precondition
 *  the emergency endpoints deliberately do not consult. */
const DENIES_OUTRIGHT = ['role_policy_denies', 'site_assignment_missing'] as const;

export function useEmergencyCapabilities(): EmergencyCapabilities {
  // As `useFireModeState`: nothing here throws without its provider. The
  // fire bar's button reads this hook (`useFirePanelButtonLabel`), and
  // the banners are mounted in tests and stories with no site or auth
  // context. The roles are the effective ones (a "view as" role replaces
  // them, `RolePreviewProvider`).
  const siteId = useContext(SiteContext)?.site?.id ?? '';
  const roles = useContext(AuthContext)?.roles ?? [];
  const query = useQuery(capabilitiesQueryOptions(siteId ? (siteId as SiteId) : null));
  const failed = query.data === undefined && query.isError;
  const held = (capability: CapabilityId): boolean => {
    // A FAILED READ FALLS BACK TO THE ROLE MIRROR (review BC-F13,
    // 2026-10-31). It used to leave every role with no control at all:
    // a supervisor in a real alarm the building did not pass on had no
    // by-hand trigger (D7), only "close this panel and retry". The
    // mirror can only say yes where the backend's role table does, so a
    // front desk still gets no button, and the server stays the
    // authority: a press it refuses shows its refusal. The same choice
    // `FireRelocalizeAction` makes, and the menu item follows it.
    if (failed) return normaliseRoles(roles).some((r) => roleHoldsCapability(r, capability));
    const cap = query.data?.capabilities.find((c) => c.capability === capability);
    if (!cap) return false;
    return !DENIES_OUTRIGHT.some((r) => cap.disabled_reasons.includes(r));
  };
  return {
    canTrigger: held('emergency.trigger'),
    canReset: held('emergency.reset'),
    loaded: query.data !== undefined,
    failed,
  };
}

/**
 * The fire bar's button, named for what this reader can do there (review
 * BC-F12, 2026-10-31). Every role may open the panel to see the fire
 * state (decided 2026-10-31), but "Emergency recovery" promised the front
 * desk an action the panel then does not offer them. A supervisor (who
 * can trigger or reset) keeps the label the manual quotes.
 */
export function useFirePanelButtonLabel(): string {
  const { canTrigger, canReset, loaded, failed } = useEmergencyCapabilities();
  // While the matrix loads, the label the manual names; it settles in a
  // moment and the panel itself never shows a control before it does.
  if (!loaded && !failed) return EMERGENCY_RECOVERY_LABEL;
  return canTrigger || canReset ? EMERGENCY_RECOVERY_LABEL : FIRE_MODE_STATUS_LABEL;
}
export const EMERGENCY_RECOVERY_LABEL = 'Emergency recovery';
export const FIRE_MODE_STATUS_LABEL = 'Fire mode status';

// ----------------------------------------------------------------------------
// "Held by fire mode", for every command path
// ----------------------------------------------------------------------------

export const FIRE_MODE_HOLD_CODE = 'fire_mode_active';

export const FIRE_MODE_HOLD_TITLE = 'Held by fire mode';

export const FIRE_MODE_HOLD_COPY =
  'This site is frozen behind an open fire event — nothing was sent. ' +
  'It is not a permissions problem and no administrator can grant it away: ' +
  'dispatch resumes when the alarm is resolved from Emergency recovery.';

/**
 * The v1 error code carried by a failed request, dug out of every
 * envelope the SPA can actually receive.
 *
 * There are three, and only the first is the one the contract
 * describes:
 *
 *  1. a bare `ApiError` — `{code, message, detail}`;
 *  2. FastAPI's wrapper — `{detail: {code, message, detail}}`. This is
 *     what the api-server ACTUALLY returns, because every handler
 *     raises `HTTPException(detail=<the ApiError>)` and Starlette
 *     serialises that under a `detail` key. `ApiClientError` reads
 *     `code`/`message` off the top level, finds neither, and so becomes
 *     `code: 'internal'` with the literal message "api error" — which
 *     is why `useSchedules.ts::isFireModeError`, comparing against
 *     `err.code`, could never once have returned true, and why a
 *     refused dispatch during an alarm showed the operator the words
 *     "api error" and nothing else. The real code survives on
 *     `ApiClientError.detail`, because the constructor's
 *     `'detail' in body` test matches FastAPI's wrapper key;
 *  3. the older ad-hoc errors in `hooks/index.ts`, which carry the
 *     parsed body on `.body` and the status on `.status`.
 */
export function apiErrorCode(err: unknown): string | null {
  if (typeof err !== 'object' || err === null) return null;
  const e = err as Record<string, unknown>;
  const direct = e.code;
  if (typeof direct === 'string' && direct !== 'internal') return direct;
  for (const nested of [e.detail, e.body]) {
    const code = nestedCode(nested);
    if (code) return code;
  }
  return typeof direct === 'string' ? direct : null;
}

function nestedCode(value: unknown): string | null {
  if (typeof value !== 'object' || value === null) return null;
  const v = value as Record<string, unknown>;
  if (typeof v.code === 'string') return v.code;
  // `{detail: {detail: {code}}}` — the ad-hoc path stores the whole
  // response body, FastAPI's wrapper is inside it.
  const inner = v.detail;
  if (typeof inner === 'object' && inner !== null) {
    const code = (inner as Record<string, unknown>).code;
    if (typeof code === 'string') return code;
  }
  return null;
}

/**
 * A refused command carrying `fire_mode_active` is a HOLD, not a
 * denial: the caller has the capability and the request was well
 * formed, but the site is frozen behind an open fire event. Rendering
 * it with the generic error copy reads as "you are not allowed to do
 * this", which sends the operator hunting a permission problem that
 * does not exist while a building is being evacuated.
 */
export function isFireModeHold(err: unknown): boolean {
  return apiErrorCode(err) === FIRE_MODE_HOLD_CODE;
}
